<template>
  <aside
    class="admin-shell admin-sidebar main-sidebar"
    :class="{ 'admin-sidebar--collapsed': collapsed }"
    aria-label="Hauptnavigation"
  >
    <router-link :to="{ name: 'Dashboard' }" custom v-slot="{ href, navigate }">
      <a
        :href="href"
        class="admin-sidebar__brand"
        aria-label="Übersicht öffnen"
        @click="select(navigate, $event)"
      >
        <img src="@/assets/casplogo.png" alt="CASP" class="admin-sidebar__logo" />
        <div v-if="!collapsed" class="admin-sidebar__brand-copy">
          <strong>WE PROTECT.</strong>
          <span>Every time. Everywhere.</span>
        </div>
      </a>
    </router-link>

    <nav class="admin-sidebar__nav" aria-label="Hauptbereiche">
      <template v-for="entry in visibleNavigation" :key="entry.key">
        <router-link
          v-if="entry.routeName"
          :to="{ name: entry.routeName }"
          custom
          v-slot="{ href, navigate }"
        >
          <a
            :href="href"
            class="admin-nav-item"
            :class="{
              'admin-nav-item--active': isActive(entry),
              'admin-nav-item--collapsed': collapsed,
            }"
            :aria-current="isActive(entry) ? 'page' : undefined"
            :title="collapsed ? entry.label : undefined"
            @click="select(navigate, $event)"
          >
            <i class="admin-nav-item__icon fa" :class="entry.icon" aria-hidden="true"></i>
            <span v-if="!collapsed" class="admin-nav-item__label">{{ entry.label }}</span>
            <span v-if="!collapsed && badgeCount(entry)" class="admin-nav-item__badge">
              {{ badgeCount(entry) }}
            </span>
          </a>
        </router-link>

        <a
          v-else-if="entry.href"
          :href="entry.href"
          class="admin-nav-item"
          :class="{ 'admin-nav-item--collapsed': collapsed }"
          :title="collapsed ? entry.label : undefined"
        >
          <i class="admin-nav-item__icon fa" :class="entry.icon" aria-hidden="true"></i>
          <span v-if="!collapsed" class="admin-nav-item__label">{{ entry.label }}</span>
        </a>

        <section v-else class="admin-nav-group">
          <button
            v-if="!collapsed"
            type="button"
            class="admin-nav-group__toggle admin-button--ghost"
            :aria-expanded="isOpen(entry)"
            :aria-controls="`main-nav-${entry.key}`"
            @click="toggleGroup(entry.key)"
          >
            <i class="fa" :class="entry.icon" aria-hidden="true"></i>
            <span>{{ entry.label }}</span>
            <i
              class="fa fa-chevron-down admin-nav-group__chevron"
              :class="{ 'admin-nav-group__chevron--open': isOpen(entry) }"
              aria-hidden="true"
            ></i>
          </button>
          <div
            v-if="collapsed || isOpen(entry)"
            :id="`main-nav-${entry.key}`"
            class="admin-nav-group__items"
            :class="{ 'admin-nav-group__items--collapsed': collapsed }"
          >
            <router-link
              v-for="item in entry.items"
              :key="item.key"
              :to="{ name: item.routeName }"
              custom
              v-slot="{ href, navigate }"
            >
              <a
                :href="href"
                class="admin-nav-item"
                :class="{
                  'admin-nav-item--active': isActive(item),
                  'admin-nav-item--collapsed': collapsed,
                }"
                :aria-current="isActive(item) ? 'page' : undefined"
                :title="collapsed ? item.label : undefined"
                @click="select(navigate, $event)"
              >
                <i class="admin-nav-item__icon fa" :class="item.icon" aria-hidden="true"></i>
                <span v-if="!collapsed" class="admin-nav-item__label">{{ item.label }}</span>
                <span v-if="!collapsed && badgeCount(item)" class="admin-nav-item__badge">
                  {{ badgeCount(item) }}
                </span>
              </a>
            </router-link>
          </div>
        </section>
      </template>
    </nav>

    <div class="admin-sidebar__footer">
      <div v-if="!collapsed && stats?.user" class="admin-sidebar__user">
        <span class="admin-sidebar__user-label">Angemeldet als</span>
        <strong>{{ stats.user.name }}</strong>
      </div>
      <button
        v-if="!collapsed"
        type="button"
        class="admin-sidebar__collapse admin-button--ghost"
        aria-label="Navigation einklappen"
        @click="$emit('toggle-collapse')"
      >
        <i class="fa fa-angles-left" aria-hidden="true"></i>
        <span>Einklappen</span>
      </button>
      <button
        v-else
        type="button"
        class="admin-sidebar__collapse admin-sidebar__collapse--icon admin-button--ghost"
        aria-label="Navigation ausklappen"
        title="Navigation ausklappen"
        @click="$emit('toggle-collapse')"
      >
        <i class="fa fa-angles-right" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        class="admin-sidebar__logout"
        :class="{ 'admin-sidebar__logout--icon': collapsed }"
        :title="collapsed ? logoutLabel : undefined"
        @click="$emit('logout')"
      >
        <i class="fa fa-right-from-bracket" aria-hidden="true"></i>
        <span v-if="!collapsed">{{ logoutLabel }}</span>
      </button>
    </div>
  </aside>
</template>

<script>
import { computed, onMounted, reactive, watch } from "vue";
import { useRoute } from "vue-router";
import { mainNavigation } from "@/mainNavigation";
import "@/assets/admin.css";
import "@/assets/main-sidebar.css";

export default {
  name: "MainSidebar",
  props: {
    stats: {
      type: Object,
      default: () => ({}),
    },
    offline: {
      type: Boolean,
      default: false,
    },
    collapsed: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["toggle-collapse", "logout"],
  setup(props) {
    const route = useRoute();
    const openGroups = reactive({});

    const visibleNavigation = computed(() =>
      mainNavigation
        .map((entry) => ({
          ...entry,
          items: entry.items?.filter(isVisible),
        }))
        .filter((entry) => !entry.items || entry.items.length)
    );

    const logoutLabel = computed(() =>
      props.stats?.impersonating ? "Ausloggen (Admin)" : "Ausloggen"
    );

    function isVisible(item) {
      if (props.offline && item.key !== "settings") return false;
      if (item.requiresAdvanced && !props.stats?.advanced) return false;
      if (item.requiresInternalDispo && !props.stats?.permissions?.internalDispo) {
        return false;
      }
      return true;
    }

    function isActive(item) {
      return route.name === item.routeName;
    }

    function isOpen(group) {
      const explicit = openGroups[group.key];
      if (explicit !== undefined) return explicit;
      return group.items?.some((item) => isActive(item)) || false;
    }

    function toggleGroup(key) {
      const group = mainNavigation.find((entry) => entry.key === key);
      openGroups[key] = !isOpen(group);
    }

    function badgeCount(item) {
      if (!item.badge) return null;
      return props.stats?.[item.badge] || null;
    }

    function select(navigate, event) {
      navigate(event);
    }

    onMounted(() => {
      visibleNavigation.value
        .filter((entry) => entry.items?.some((item) => isActive(item)))
        .forEach((entry) => {
          openGroups[entry.key] = true;
        });
    });

    watch(
      () => route.name,
      () => {
        visibleNavigation.value
          .filter((entry) => entry.items?.some((item) => isActive(item)))
          .forEach((entry) => {
            openGroups[entry.key] = true;
          });
      }
    );

    return {
      visibleNavigation,
      logoutLabel,
      isActive,
      isOpen,
      toggleGroup,
      badgeCount,
      select,
    };
  },
};
</script>
